/**
 * Body Insight Guard Component
 *
 * Access control for body insight pages:
 * 1. User must be authenticated
 * 2. Onboarding must be complete (pages_completed includes 'struggles')
 * 3. Body insights must not already be completed
 *
 * Feature: 017-body-insight-summary
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface BodyInsightGuardProps {
  children: React.ReactNode;
}

export const BodyInsightGuard: React.FC<BodyInsightGuardProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  // Wait for auth to load
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF5E9]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C98CCC]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check 1: User must be authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check 2: Onboarding must be complete
  const onboardingComplete = user.onboarding?.pages_completed?.includes('struggles');
  if (!onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check 3: Body insights must not already be completed
  // If onboarding.completed is true OR body_insight_completed_at is set, redirect to home
  const bodyInsightCompleted =
    user.onboarding?.completed === true ||
    user.onboarding?.body_insight_completed_at !== null;
  if (bodyInsightCompleted) {
    // If user already completed body insights, redirect to home
    return <Navigate to="/" replace />;
  }

  // All checks passed - render children
  return <>{children}</>;
};
